import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  XCircle,
  Trash2,
  Loader2,
  Shield,
  LogOut,
  Search,
  Clock,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ApplicationsPanel } from "@/components/admin/ApplicationsPanel";
import { MaintenancePanel } from "@/components/admin/MaintenancePanel";
import { ProgramsPanel } from "@/components/admin/ProgramsPanel";
import { FeedbackPanel } from "@/components/admin/FeedbackPanel";
import { BetaInsightsPanel } from "@/components/admin/BetaInsightsPanel";

type MessageStatus = "pending" | "approved" | "rejected";

const Admin = () => {
  const { user, isAdmin, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [searchQuery, setSearchQuery] = useState("");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    navigate("/admin/login", { replace: true });
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center space-y-4">
            <Shield className="h-16 w-16 text-destructive mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
            <p className="text-muted-foreground">You don't have admin privileges.</p>
            <Button variant="outline" onClick={() => signOut().then(() => navigate("/"))}>
              Sign Out & Go Home
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return <AdminDashboard statusFilter={statusFilter} setStatusFilter={setStatusFilter} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />;
};

const AdminDashboard = ({
  statusFilter,
  setStatusFilter,
  searchQuery,
  setSearchQuery,
}: {
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
}) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const activeTab =
    location.pathname === "/admin/intern-applications"
      ? "applications"
      : location.pathname === "/admin/programs"
      ? "programs"
      : location.pathname === "/admin/maintenance"
      ? "maintenance"
      : location.pathname === "/admin/feedback"
      ? "feedback"
      : location.pathname === "/admin/beta"
      ? "beta"
      : "messages";

  const handleTabChange = (value: string) => {
    if (value === "applications") {
      navigate("/admin/intern-applications", { replace: false });
    } else if (value === "programs") {
      navigate("/admin/programs", { replace: false });
    } else if (value === "maintenance") {
      navigate("/admin/maintenance", { replace: false });
    } else if (value === "feedback") {
      navigate("/admin/feedback", { replace: false });
    } else if (value === "beta") {
      navigate("/admin/beta", { replace: false });
    } else {
      navigate("/admin/messages", { replace: false });
    }
  };

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["admin-messages", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("whatsapp_messages")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter as MessageStatus);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: MessageStatus }) => {
      const { error } = await supabase
        .from("whatsapp_messages")
        .update({ status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      toast.success(`Message ${status}!`);
      queryClient.invalidateQueries({ queryKey: ["admin-messages"] });
    },
    onError: () => toast.error("Failed to update status."),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("whatsapp_messages")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Message deleted.");
      queryClient.invalidateQueries({ queryKey: ["admin-messages"] });
    },
    onError: () => toast.error("Failed to delete."),
  });

  const filteredMessages = messages.filter(
    (m) =>
      m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingCount = messages.filter((m) => m.status === "pending").length;

  const statusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending</Badge>;
      case "approved":
        return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <Header />
      <main className="flex-1">
        {/* Admin Header */}
        <section className="bg-background border-b border-border">
          <div className="container py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="h-5 w-5 text-primary" />
                  <h1 className="text-2xl font-bold text-foreground">Moderation Panel</h1>
                </div>
                <p className="text-sm text-muted-foreground">
                  Signed in as {user?.email} • {pendingCount} pending review
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => signOut().then(() => navigate("/"))}
                className="gap-2 self-start"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </section>

        <section className="py-6">
          <div className="container">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger value="messages">WhatsApp Messages</TabsTrigger>
                <TabsTrigger value="applications">Intern Applications</TabsTrigger>
                <TabsTrigger value="programs">Programs</TabsTrigger>
                <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
                <TabsTrigger value="feedback">Feedback & Insights</TabsTrigger>
                <TabsTrigger value="beta">Beta Insights</TabsTrigger>
              </TabsList>

              <TabsContent value="messages">
                <div className="mb-4 flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search messages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredMessages.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No messages</p>
                <p className="text-sm">Nothing to review in this category.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredMessages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-5 rounded-xl bg-card border border-border"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {statusBadge(message.status)}
                          <Badge variant="sector">{message.category}</Badge>
                          {message.submitted_by_name && (
                            <span className="text-xs text-muted-foreground">
                              by {message.submitted_by_name}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {new Date(message.created_at).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                        <h3 className="font-semibold text-foreground mb-2">{message.title}</h3>
                        <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-sans bg-muted/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                          {message.content}
                        </pre>
                        <p className="text-xs text-muted-foreground mt-2">Source: {message.source}</p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex sm:flex-col gap-2 shrink-0">
                        {message.status !== "approved" && (
                          <Button
                            size="sm"
                            onClick={() =>
                              updateStatusMutation.mutate({ id: message.id, status: "approved" })
                            }
                            disabled={updateStatusMutation.isPending}
                            className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Approve
                          </Button>
                        )}
                        {message.status !== "rejected" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateStatusMutation.mutate({ id: message.id, status: "rejected" })
                            }
                            disabled={updateStatusMutation.isPending}
                            className="gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50"
                          >
                            <XCircle className="h-4 w-4" />
                            Reject
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (confirm("Delete this message permanently?")) {
                              deleteMutation.mutate(message.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

              </TabsContent>

              <TabsContent value="applications">
                <ApplicationsPanel />
              </TabsContent>

              <TabsContent value="programs">
                <ProgramsPanel />
              </TabsContent>

              <TabsContent value="maintenance">
                <MaintenancePanel />
              </TabsContent>

              <TabsContent value="feedback">
                <FeedbackPanel />
              </TabsContent>

              <TabsContent value="beta">
                <BetaInsightsPanel />
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Admin;
